import {
  DealEnum as GQLDealEnum,
  type SettlementDealInput as GQLInput,
  type SettlementDeal as GQLItem,
} from '../../../generated/api.graphql';
import { take } from '../../../util';

/** The deal structures a settlement line can take. */
export const DealEnum = Object.freeze({
  ArtistGuarantee: GQLDealEnum.ArtistGuarantee,
  ArtistGuaranteePlusGborAfterExpenses: GQLDealEnum.ArtistGuaranteePlusGborAfterExpenses,
  ArtistGuaranteeVsGbor: GQLDealEnum.ArtistGuaranteeVsGbor,
  ArtistGuaranteeVsGborAfterExpenses: GQLDealEnum.ArtistGuaranteeVsGborAfterExpenses,
  FlatFee: GQLDealEnum.FlatFee,
  Gbor: GQLDealEnum.Gbor,
  GborAfterExpenses: GQLDealEnum.GborAfterExpenses,
  GuaranteeToVenue: GQLDealEnum.GuaranteeToVenue,
  None: GQLDealEnum.None,
  PercentAfterHit: GQLDealEnum.PercentAfterHit,
  PercentWithHit: GQLDealEnum.PercentWithHit,
  Rental: GQLDealEnum.Rental,
  SalesPercentage: GQLDealEnum.SalesPercentage,
});

export type DealEnum = (typeof DealEnum)[keyof typeof DealEnum];

export type SettlementDeal = {
  id?: number;
  dealType?: DealEnum;
  amount?: number;
  percent?: number;
  expenseAmount?: number;
  firstAmount?: number;
  hitAmount?: number;
  showTicketSales?: boolean;
};

/**
 * A deal line on the way back. Spelled out rather than derived from the read shape: the two answer
 * to different schemas -- this one is bounded by `SettlementDealInput`, which `toGqlInput`
 * whitelists against -- so an intersection would grow the input with whatever the output grows.
 * The settlement's other row inputs follow the same rule.
 */
export type DealInput = {
  id?: number;
  dealType?: DealEnum;
  amount?: number;
  percent?: number;
  expenseAmount?: number;
  firstAmount?: number;
  hitAmount?: number;
  showTicketSales?: boolean;
  _destroy?: boolean;
};

export const fromGql = (item: GQLItem): SettlementDeal => ({
  id: item.id,
  dealType: item.dealType ?? undefined,
  amount: item.amount ?? undefined,
  percent: item.percent ?? undefined,
  expenseAmount: item.expenseAmount ?? undefined,
  firstAmount: item.firstAmount ?? undefined,
  hitAmount: item.hitAmount ?? undefined,
  showTicketSales: item.showTicketSales ?? undefined,
});

/**
 * The nine members of `SettlementDealInput` the settlement form owns. (`dealId` is a tenth and is
 * never sent -- `dealType` carries the structure and the schema derives the template from it.)
 *
 * The whitelist is load-bearing, not tidiness: `SettlementDeal.vue`'s `dealToSave` spreads a whole
 * *server* deal object into what it hands the store, and `SettlementDealInput` declares none of
 * `deal`, `attachments` or the timestamps -- an unfiltered payload is rejected outright and every
 * deal save fails. Owning it beside the type makes it hold whatever a caller passes.
 */
const GQL_FIELDS = [
  'id',
  '_destroy',
  'amount',
  'dealType',
  'expenseAmount',
  'firstAmount',
  'hitAmount',
  'percent',
  'showTicketSales',
] as const;

/**
 * `take` **omits** keys the caller did not set rather than nulling them, the opposite of the tax
 * section's rule. That is deliberate: the resolver feeds `deals.map(&:to_h)` into
 * `event_artist_deals_attributes`, where an explicit nil writes and an omitted key does not, these
 * are monetary columns, and there is no deal-clearing requirement to satisfy.
 */
export const toGqlInput = (deal: DealInput): GQLInput =>
  take<GQLInput, DealInput>(deal, ...GQL_FIELDS);
